import { useCallback, useMemo } from 'react';
import { differenceInCalendarDays, parse } from 'date-fns';
import { dateSortArray } from 'c-lib';
import { ChartDataKeyDateFormat } from 'c-reports/Generators';
import type { ReportDateTimeframe } from 'c-reports/Types';
import type { ElasticSearchFrameIdMapDataResponse, PCAAnalyticsReportResponse } from 'c-sdk';
import { EsPCAReportField, PCAReportMetric } from 'c-sdk';
import { apiClient } from 'c-data';
import { ByFieldOverall } from 'c-main/Components/Campaign/CampaignReports/Views/ByField/byFieldDataMutators';
import { mapDataToSqlMapData } from 'c-main/Components/Campaign/CampaignReports/ElasticSearch/Transformers';
import { sortByNameCustomField } from 'c-main/Components/Campaign/CampaignReports/Views/ByField/ChartTypes/sorting';
import { callApi } from 'c-data-layer';
import type { SummaryWidgetConfig } from './summaryCustomizationConfig';
import {
    flattenGroupedStats,
    foldByFieldReportData,
    pickGroupedWidgetPayload,
} from './campaignAnalyticsUtils';
import type { TargetSeriesParam } from './targetPacing';
import { applyTargetExpectedBars, applyTargetExpectedLine } from './targetPacing';

type Params = {
    campaignExpectedImpacts: number | null | undefined;
    campaignStart: Date;
    campaignEnd: Date;
    campaignAsOfDate: Date;
    windowStart: Date;
    isDsp: boolean;
    isAverageAudienceMode: boolean;
    isGeopathOnly: boolean;
    selectedAudienceLabelForCharts: string | null;
    hasUserActiveFilters: boolean;
    dspAudienceFilterCount: number;
    data: PCAAnalyticsReportResponse | null;
    dateLabelLabel: string;
    actualLabelImpacts: string;
    actualDeliveredLabelImpacts: string;
    shareOfTotalLabel: string;
    percentageLabelImpacts: string;
    expectedLabelForImpactsCharts: string;
    missingLabelImpacts: string;
    actualLabelPlays: string;
    percentageLabelPlays: string;
    expectedLabelPlays: string;
    missingLabelPlays: string;
    performanceLabelImpacts: string;
    inScheduleLabel: string;
    outScheduleLabel: string;
    unbookedLabel: string;
    dayMonthYearFormat: string;
    averageDailyLookup: Record<string, number>;
    geopathDailyLookup: Record<string, number>;
    averageDowLookup: Record<string, number>;
    geopathDowLookup: Record<string, number>;
    averageHourLookup: Record<string, number>;
    geopathHourLookup: Record<string, number>;
    averageRegionLookup: Record<string, number>;
    geopathRegionLookup: Record<string, number>;
    averageMediaOwnerLookup: Record<string, number>;
    geopathMediaOwnerLookup: Record<string, number>;
    applyComparisonExpected: (rows: any[] | null, lookup: Record<string, number>) => any[] | null;
    buildGroupByFieldDateRange: (
        start: Date,
        end: Date,
        tf: ReportDateTimeframe,
    ) => {
        startDate: string;
        endDate: string;
    };
    buildMetricBarRow: (label: string, stats: any, metric: SummaryWidgetConfig['metric']) => any;
    fieldFilters: Record<string, string[]>;
    environmentMode: string | null | undefined;
    source: string;
    id: number;
    dispatch: any;
    showDefaultDspExpectedLine: (widget: SummaryWidgetConfig) => boolean;
    showDefaultDspExpectedBar: (widget: SummaryWidgetConfig) => boolean;
    showDefaultDspTargetPercent: (widget: SummaryWidgetConfig) => boolean;
    summaryWidgetGroupByEsFieldMap: Record<string, EsPCAReportField>;
    filteredMapData:
        | Record<string, ElasticSearchFrameIdMapDataResponse | undefined>
        | null
        | undefined;
    currentFilteredMapDataKey: string;
    /**
     * UL-8133 -- set only for an insertion-order targeting campaign (`vistar_us2`). Its expected
     * line and bars come from the per-IO targets, never from even pacing of `expected_impacts`.
     */
    targetSeries?: TargetSeriesParam | null;
};

export const useCampaignAnalyticsWidgetData = ({
    campaignExpectedImpacts,
    campaignStart,
    campaignEnd,
    campaignAsOfDate,
    windowStart,
    isDsp,
    isAverageAudienceMode,
    isGeopathOnly,
    selectedAudienceLabelForCharts,
    hasUserActiveFilters,
    dspAudienceFilterCount,
    data,
    dateLabelLabel,
    actualLabelImpacts,
    actualDeliveredLabelImpacts,
    shareOfTotalLabel,
    percentageLabelImpacts,
    expectedLabelForImpactsCharts,
    missingLabelImpacts,
    actualLabelPlays,
    percentageLabelPlays,
    expectedLabelPlays,
    missingLabelPlays,
    performanceLabelImpacts,
    inScheduleLabel,
    outScheduleLabel,
    unbookedLabel,
    dayMonthYearFormat,
    averageDailyLookup,
    geopathDailyLookup,
    averageDowLookup,
    geopathDowLookup,
    averageHourLookup,
    geopathHourLookup,
    averageRegionLookup,
    geopathRegionLookup,
    averageMediaOwnerLookup,
    geopathMediaOwnerLookup,
    applyComparisonExpected,
    buildGroupByFieldDateRange,
    buildMetricBarRow,
    fieldFilters,
    environmentMode,
    source,
    id,
    dispatch,
    showDefaultDspExpectedLine,
    showDefaultDspExpectedBar,
    showDefaultDspTargetPercent,
    summaryWidgetGroupByEsFieldMap,
    filteredMapData,
    currentFilteredMapDataKey,
    targetSeries = null,
}: Params) => {
    const targetLabels = useMemo(
        () => ({ dateLabel: dateLabelLabel, expectedLabel: expectedLabelForImpactsCharts }),
        [dateLabelLabel, expectedLabelForImpactsCharts],
    );
    const targetDailyExpected = targetSeries?.dailyExpected ?? null;
    const isTargetCampaign = targetSeries != null;
    // Where even pacing would apply, a vistar_us2 campaign shows its targets instead -- or, under a
    // filter targets can't be split by, no expected figure at all.
    const showTargetExpected =
        isTargetCampaign &&
        targetSeries.showTargets &&
        !isAverageAudienceMode &&
        dspAudienceFilterCount === 0;

    const applyPacedExpectedLine = useCallback(
        (rows: any[] | null) => {
            if (isTargetCampaign) {
                return applyTargetExpectedLine(rows, targetDailyExpected, targetLabels);
            }
            if (!rows || campaignExpectedImpacts == null) return rows;

            const totalDays = differenceInCalendarDays(campaignEnd, campaignStart) + 1;
            if (totalDays <= 0) return rows;

            const dailyExpected = campaignExpectedImpacts / totalDays;
            const lastCompletedDate = campaignAsOfDate;

            return rows.map(row => {
                const rawDate = row?.[dateLabelLabel];
                const rowDate = parse(rawDate, ChartDataKeyDateFormat, new Date());

                if (Number.isNaN(rowDate.getTime()) || lastCompletedDate < campaignStart) {
                    return { ...row, [expectedLabelForImpactsCharts]: 0 };
                }

                const cappedDate = rowDate > lastCompletedDate ? lastCompletedDate : rowDate;
                const elapsedDays = differenceInCalendarDays(cappedDate, windowStart) + 1;

                return {
                    ...row,
                    [expectedLabelForImpactsCharts]:
                        elapsedDays > 0 ? dailyExpected * Math.min(elapsedDays, totalDays) : 0,
                };
            });
        },
        [
            campaignAsOfDate,
            campaignEnd,
            campaignExpectedImpacts,
            campaignStart,
            dateLabelLabel,
            expectedLabelForImpactsCharts,
            isTargetCampaign,
            targetDailyExpected,
            targetLabels,
            windowStart,
        ],
    );

    const adjustedCampaignTarget = useMemo(() => {
        const expectedImpacts = campaignExpectedImpacts;

        // Never an even-paced figure for vistar_us2: its headline comes from the targets.
        if (isTargetCampaign) return null;
        if (!isDsp || expectedImpacts == null) return expectedImpacts ?? null;

        const totalDays = differenceInCalendarDays(campaignEnd, campaignStart) + 1;
        if (totalDays <= 0) return expectedImpacts;

        const completedDate = campaignAsOfDate;

        if (completedDate < campaignStart) return 0;
        if (completedDate >= campaignEnd) return expectedImpacts;

        const elapsedDays = differenceInCalendarDays(completedDate, campaignStart) + 1;
        return (expectedImpacts / totalDays) * Math.max(0, elapsedDays);
    }, [
        campaignAsOfDate,
        campaignEnd,
        campaignExpectedImpacts,
        campaignStart,
        isDsp,
        isTargetCampaign,
    ]);

    const applyTargetPercent = useCallback(
        (rows: any[] | null) => {
            if (!rows || rows.length === 0) return rows;

            const totalDelivered = rows.reduce(
                (sum, row) => sum + Number(row?.[actualLabelImpacts] ?? 0),
                0,
            );
            if (totalDelivered <= 0) return rows;

            return rows.map(row => {
                const actual = Number(row?.[actualLabelImpacts] ?? 0);
                const pct = (actual / totalDelivered) * 100;

                return {
                    ...row,
                    [`${actualLabelImpacts}__pct`]: pct,
                    [`${actualDeliveredLabelImpacts}__pct`]: pct,
                    [`${actualLabelImpacts}__baseLabel`]: shareOfTotalLabel,
                    [`${actualDeliveredLabelImpacts}__baseLabel`]: shareOfTotalLabel,
                    [`${actualLabelImpacts}__baseValue`]: totalDelivered,
                    [`${actualDeliveredLabelImpacts}__baseValue`]: totalDelivered,
                };
            });
        },
        [actualDeliveredLabelImpacts, actualLabelImpacts, shareOfTotalLabel],
    );

    const fetchExtraWidgetData = useCallback(
        async (widget: SummaryWidgetConfig, start: Date, end: Date, tf: ReportDateTimeframe) => {
            if (widget.source !== 'groupByField' || widget.groupBy.length === 0) return [];

            const fields = widget.groupBy
                .map(groupBy => summaryWidgetGroupByEsFieldMap[groupBy])
                .filter(Boolean) as EsPCAReportField[];

            if (fields.length !== widget.groupBy.length) return [];

            try {
                const requestData = apiClient.ReportsElasticSearch.pcaGroupByField({
                    campaignId: id,
                    ...buildGroupByFieldDateRange(start, end, tf),
                    environment: environmentMode,
                    fieldFilters,
                    showStats: true,
                    source,
                    ...(fields.length === 1 ? { field: fields[0] } : { fields }),
                });
                const [, success] = await dispatch(callApi(requestData.request));
                const response = success;

                const rawGroupedData = pickGroupedWidgetPayload(response);
                const rows = flattenGroupedStats(rawGroupedData, [], isDsp).map(
                    ({ label, path, stats }) => ({
                        ...buildMetricBarRow(label, stats, widget.metric),
                        exportGroupValues: path,
                    }),
                );

                const sortField = fields.length === 1 ? fields[0] : '';
                const sortedRows = sortField
                    ? sortByNameCustomField(rows, dateLabelLabel, sortField, dayMonthYearFormat)
                    : rows;

                return showDefaultDspExpectedLine(widget)
                    ? applyPacedExpectedLine(sortedRows)
                    : showDefaultDspTargetPercent(widget)
                    ? applyTargetPercent(sortedRows)
                    : sortedRows;
            } catch {
                return [];
            }
        },
        [
            applyPacedExpectedLine,
            applyTargetPercent,
            buildGroupByFieldDateRange,
            buildMetricBarRow,
            dateLabelLabel,
            dayMonthYearFormat,
            dispatch,
            environmentMode,
            fieldFilters,
            id,
            isDsp,
            showDefaultDspExpectedLine,
            showDefaultDspTargetPercent,
            source,
            summaryWidgetGroupByEsFieldMap,
        ],
    );

    const dailyImpactsData = useMemo(() => {
        if (!data) return null;

        const expectedOverride =
            isDsp && isAverageAudienceMode
                ? averageDailyLookup
                : isDsp && (isGeopathOnly || selectedAudienceLabelForCharts != null)
                ? geopathDailyLookup
                : undefined;

        const rows = ByFieldOverall(
            { ...data, reportData: data.dailyImpacts },
            Object.keys(data?.dailyImpacts ?? {}),
            actualLabelImpacts,
            percentageLabelImpacts,
            expectedLabelForImpactsCharts,
            missingLabelImpacts,
            dateLabelLabel,
            inScheduleLabel,
            outScheduleLabel,
            unbookedLabel,
            true,
            expectedOverride,
        ).sort(dateSortArray(dateLabelLabel, ChartDataKeyDateFormat));

        if (isTargetCampaign) return showTargetExpected ? applyPacedExpectedLine(rows) : rows;

        return isDsp &&
            !hasUserActiveFilters &&
            !isAverageAudienceMode &&
            dspAudienceFilterCount === 0
            ? applyPacedExpectedLine(rows)
            : rows;
    }, [
        actualLabelImpacts,
        applyPacedExpectedLine,
        isTargetCampaign,
        showTargetExpected,
        averageDailyLookup,
        data,
        dateLabelLabel,
        dspAudienceFilterCount,
        expectedLabelForImpactsCharts,
        hasUserActiveFilters,
        inScheduleLabel,
        isAverageAudienceMode,
        isDsp,
        isGeopathOnly,
        selectedAudienceLabelForCharts,
        geopathDailyLookup,
        missingLabelImpacts,
        outScheduleLabel,
        percentageLabelImpacts,
        unbookedLabel,
    ]);

    const applyDailyExpectedBars = useCallback(
        (rows: any[] | null) => {
            if (isTargetCampaign) {
                return applyTargetExpectedBars(rows, targetDailyExpected, targetLabels);
            }
            if (!rows || campaignExpectedImpacts == null) return rows;

            const totalDays = differenceInCalendarDays(campaignEnd, campaignStart) + 1;
            if (totalDays <= 0) return rows;

            const dailyExpected = campaignExpectedImpacts / totalDays;
            const lastCompletedDate = campaignAsOfDate;

            return rows.map(row => {
                const rawDate = row?.[dateLabelLabel];
                const rowDate = parse(rawDate, ChartDataKeyDateFormat, new Date());

                if (
                    Number.isNaN(rowDate.getTime()) ||
                    lastCompletedDate < campaignStart ||
                    rowDate > lastCompletedDate
                ) {
                    return { ...row, [expectedLabelForImpactsCharts]: 0 };
                }

                return {
                    ...row,
                    [expectedLabelForImpactsCharts]: dailyExpected,
                };
            });
        },
        [
            campaignAsOfDate,
            campaignEnd,
            campaignExpectedImpacts,
            campaignStart,
            dateLabelLabel,
            expectedLabelForImpactsCharts,
            isTargetCampaign,
            targetDailyExpected,
            targetLabels,
        ],
    );

    const dailyImpactsBarData = useMemo(() => {
        if (!data) return null;

        const expectedOverride =
            isDsp && isAverageAudienceMode
                ? averageDailyLookup
                : isDsp && (isGeopathOnly || selectedAudienceLabelForCharts != null)
                ? geopathDailyLookup
                : undefined;

        const rows = ByFieldOverall(
            { ...data, reportData: data.dailyImpacts },
            Object.keys(data?.dailyImpacts ?? {}),
            actualLabelImpacts,
            percentageLabelImpacts,
            expectedLabelForImpactsCharts,
            missingLabelImpacts,
            dateLabelLabel,
            inScheduleLabel,
            outScheduleLabel,
            unbookedLabel,
            false,
            expectedOverride,
        ).sort(dateSortArray(dateLabelLabel, ChartDataKeyDateFormat));

        if (isTargetCampaign) return showTargetExpected ? applyDailyExpectedBars(rows) : rows;

        return isDsp &&
            !hasUserActiveFilters &&
            !isAverageAudienceMode &&
            dspAudienceFilterCount === 0
            ? applyDailyExpectedBars(rows)
            : rows;
    }, [
        actualLabelImpacts,
        applyDailyExpectedBars,
        isTargetCampaign,
        showTargetExpected,
        averageDailyLookup,
        data,
        dateLabelLabel,
        dspAudienceFilterCount,
        expectedLabelForImpactsCharts,
        hasUserActiveFilters,
        inScheduleLabel,
        isAverageAudienceMode,
        isDsp,
        isGeopathOnly,
        selectedAudienceLabelForCharts,
        geopathDailyLookup,
        missingLabelImpacts,
        outScheduleLabel,
        percentageLabelImpacts,
        unbookedLabel,
    ]);

    const dailyPlaysData = useMemo(
        () =>
            data
                ? ByFieldOverall(
                      { ...data, reportData: data.dailyPlays },
                      Object.keys(data?.dailyPlays ?? {}),
                      actualLabelPlays,
                      percentageLabelPlays,
                      expectedLabelPlays,
                      missingLabelPlays,
                      dateLabelLabel,
                      inScheduleLabel,
                      outScheduleLabel,
                      unbookedLabel,
                      true,
                  ).sort(dateSortArray(dateLabelLabel, ChartDataKeyDateFormat))
                : null,
        [
            actualLabelPlays,
            data,
            dateLabelLabel,
            expectedLabelPlays,
            inScheduleLabel,
            missingLabelPlays,
            outScheduleLabel,
            percentageLabelPlays,
            unbookedLabel,
        ],
    );

    const dailyPlaysBarData = useMemo(
        () =>
            data
                ? ByFieldOverall(
                      { ...data, reportData: data.dailyPlays },
                      Object.keys(data?.dailyPlays ?? {}),
                      actualLabelPlays,
                      percentageLabelPlays,
                      expectedLabelPlays,
                      missingLabelPlays,
                      dateLabelLabel,
                      inScheduleLabel,
                      outScheduleLabel,
                      unbookedLabel,
                      false,
                  ).sort(dateSortArray(dateLabelLabel, ChartDataKeyDateFormat))
                : null,
        [
            actualLabelPlays,
            data,
            dateLabelLabel,
            expectedLabelPlays,
            inScheduleLabel,
            missingLabelPlays,
            outScheduleLabel,
            percentageLabelPlays,
            unbookedLabel,
        ],
    );

    const byFieldData = useCallback(
        (
            key: keyof PCAAnalyticsReportResponse,
            field: EsPCAReportField,
            actual: string,
            percentage: string,
            expected: string,
            missing: string,
        ) => {
            if (!data) return null;
            const reportData = isDsp ? foldByFieldReportData(data[key] as any) : (data[key] as any);
            return sortByNameCustomField(
                ByFieldOverall(
                    { ...data, reportData },
                    Object.keys(reportData ?? {}),
                    actual,
                    percentage,
                    expected,
                    missing,
                    dateLabelLabel,
                    inScheduleLabel,
                    outScheduleLabel,
                    unbookedLabel,
                ),
                dateLabelLabel,
                field,
                dayMonthYearFormat,
            );
        },
        [
            data,
            dateLabelLabel,
            dayMonthYearFormat,
            inScheduleLabel,
            isDsp,
            outScheduleLabel,
            unbookedLabel,
        ],
    );

    const byFieldImpactsData = useCallback(
        (key: keyof PCAAnalyticsReportResponse, field: EsPCAReportField) =>
            byFieldData(
                key,
                field,
                actualLabelImpacts,
                percentageLabelImpacts,
                expectedLabelForImpactsCharts,
                missingLabelImpacts,
            ),
        [
            actualLabelImpacts,
            byFieldData,
            expectedLabelForImpactsCharts,
            missingLabelImpacts,
            percentageLabelImpacts,
        ],
    );

    const byFieldPlaysData = useCallback(
        (key: keyof PCAAnalyticsReportResponse, field: EsPCAReportField) =>
            byFieldData(
                key,
                field,
                actualLabelPlays,
                percentageLabelPlays,
                expectedLabelPlays,
                missingLabelPlays,
            ),
        [
            actualLabelPlays,
            byFieldData,
            expectedLabelPlays,
            missingLabelPlays,
            percentageLabelPlays,
        ],
    );

    const dayOfWeekImpactsData = useMemo(() => {
        const base = byFieldImpactsData('dayOfWeek', EsPCAReportField.Dow);
        return applyComparisonExpected(
            base as any,
            isAverageAudienceMode ? averageDowLookup : geopathDowLookup,
        );
    }, [
        applyComparisonExpected,
        averageDowLookup,
        byFieldImpactsData,
        geopathDowLookup,
        isAverageAudienceMode,
    ]);

    const dayOfWeekPlaysData = useMemo(
        () => byFieldPlaysData('dayOfWeek', EsPCAReportField.Dow),
        [byFieldPlaysData],
    );

    const hourOfDayImpactsData = useMemo(() => {
        const base = byFieldImpactsData('hour', EsPCAReportField.Hour);
        return applyComparisonExpected(
            base as any,
            isAverageAudienceMode ? averageHourLookup : geopathHourLookup,
        );
    }, [
        applyComparisonExpected,
        averageHourLookup,
        byFieldImpactsData,
        geopathHourLookup,
        isAverageAudienceMode,
    ]);

    const hourOfDayPlaysData = useMemo(
        () => byFieldPlaysData('hour', EsPCAReportField.Hour),
        [byFieldPlaysData],
    );

    const regionsImpactsData = useMemo(() => {
        const base = byFieldImpactsData('tvRegion', EsPCAReportField.DisplayTvRegion);
        return applyComparisonExpected(
            base as any,
            isAverageAudienceMode ? averageRegionLookup : geopathRegionLookup,
        );
    }, [
        applyComparisonExpected,
        averageRegionLookup,
        byFieldImpactsData,
        geopathRegionLookup,
        isAverageAudienceMode,
    ]);

    const regionsPlaysData = useMemo(
        () => byFieldPlaysData('tvRegion', EsPCAReportField.DisplayTvRegion),
        [byFieldPlaysData],
    );

    const mediaOwnerImpactsData = useMemo(() => {
        const base = byFieldImpactsData('mediaOwner', EsPCAReportField.DisplayOwner);
        return applyComparisonExpected(
            base as any,
            isAverageAudienceMode ? averageMediaOwnerLookup : geopathMediaOwnerLookup,
        );
    }, [
        applyComparisonExpected,
        averageMediaOwnerLookup,
        byFieldImpactsData,
        geopathMediaOwnerLookup,
        isAverageAudienceMode,
    ]);

    const mediaOwnerPlaysData = useMemo(
        () => byFieldPlaysData('mediaOwner', EsPCAReportField.DisplayOwner),
        [byFieldPlaysData],
    );

    const getWidgetData = useCallback(
        (widget: SummaryWidgetConfig) => {
            const isDateBarWidget =
                widget.kind === 'bar' &&
                widget.groupBy.length === 1 &&
                widget.groupBy[0] === 'date';

            if (
                widget.kind === 'line' &&
                widget.groupBy.length === 1 &&
                widget.groupBy[0] === 'date'
            ) {
                return widget.metric === 'plays' ? dailyPlaysData : dailyImpactsData;
            }

            if (widget.kind === 'map') {
                return mapDataToSqlMapData(
                    widget.metric === 'plays' ? PCAReportMetric.Plays : PCAReportMetric.Impacts,
                    filteredMapData?.[currentFilteredMapDataKey],
                ).reportData;
            }

            if (
                (widget.source === 'summary' || isDateBarWidget) &&
                widget.kind === 'bar' &&
                widget.groupBy.length === 1
            ) {
                if (isDateBarWidget) {
                    if (widget.metric === 'plays') return dailyPlaysBarData;
                    return showDefaultDspExpectedBar(widget)
                        ? dailyImpactsBarData
                        : dailyImpactsBarData;
                }

                const withTargetPercent = (rows: any[] | null) =>
                    showDefaultDspTargetPercent(widget) ? applyTargetPercent(rows) : rows;

                switch (widget.groupBy[0]) {
                    case 'display_owner':
                        return widget.metric === 'plays'
                            ? mediaOwnerPlaysData
                            : withTargetPercent(mediaOwnerImpactsData as any);
                    case 'display_tv_region':
                        return widget.metric === 'plays'
                            ? regionsPlaysData
                            : withTargetPercent(regionsImpactsData as any);
                    case 'day_of_week':
                        return widget.metric === 'plays'
                            ? dayOfWeekPlaysData
                            : withTargetPercent(dayOfWeekImpactsData as any);
                    case 'hour_of_day':
                        return widget.metric === 'plays'
                            ? hourOfDayPlaysData
                            : withTargetPercent(hourOfDayImpactsData as any);
                    default:
                        return [];
                }
            }

            return [];
        },
        [
            currentFilteredMapDataKey,
            dailyImpactsBarData,
            dailyImpactsData,
            dailyPlaysBarData,
            dailyPlaysData,
            dayOfWeekImpactsData,
            dayOfWeekPlaysData,
            hourOfDayImpactsData,
            hourOfDayPlaysData,
            filteredMapData,
            applyTargetPercent,
            mediaOwnerImpactsData,
            mediaOwnerPlaysData,
            regionsImpactsData,
            regionsPlaysData,
            showDefaultDspExpectedBar,
            showDefaultDspTargetPercent,
        ],
    );

    return {
        adjustedCampaignTarget,
        fetchExtraWidgetData,
        getWidgetData,
    };
};
